import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Button,
  IconButton,
  InlineLoading,
  Modal,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  TextArea,
  TextInput,
  Toggle,
} from '@carbon/react';
import { ChevronDown, ChevronUp, Edit, TrashCan } from '@carbon/icons-react';
import {
  DataTable,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from 'carbon-components-react';
import { useAppSelector } from '../../../app/hooks';
import { selectToken } from '../../../AppSlice';
import { endpoints, GatewayIp } from '../../../data/endpoints';

// ---- Types ----

interface CambriaProfile {
  uuid: string;
  label: string;
  description: string;
  profile?: string;
}

interface CambriaEndpoint {
  id: number;
  label: string;
  baseUrl: string;
  apiKey: string | null;
  priority: number;
  enabled: boolean;
}

const cambriaProfilesHeaders = [
  { key: 'label', header: 'Label' },
  { key: 'description', header: 'Description' },
  { key: 'actions', header: '' },
];

const cambriaEndpointsHeaders = [
  { key: 'priority', header: 'Priority' },
  { key: 'label', header: 'Label' },
  { key: 'baseUrl', header: 'Base URL' },
  { key: 'apiKey', header: 'API Key' },
  { key: 'status', header: 'Status' },
  { key: 'actions', header: '' },
];

// ---- Profiles Panel ----

const CambriaProfilesPanel: React.FC<{ token: string | null }> = ({ token }) => {
  const [profiles, setProfiles] = useState<CambriaProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState({ label: '', description: '', profile: '' });
  const [saving, setSaving] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${GatewayIp}${endpoints.thirdparty.cambriaProfiles}`, { headers: authHeaders });
      setProfiles(res.data);
    } catch {
      setError('Failed to load profiles.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  const openCreate = () => {
    setEditingId(null);
    setDraft({ label: '', description: '', profile: '' });
    setModalOpen(true);
  };

  const openEdit = async (uuid: string) => {
    setError(null);
    try {
      const res = await axios.get(`${GatewayIp}${endpoints.thirdparty.cambriaProfile(uuid)}`, { headers: authHeaders });
      const p = res.data as CambriaProfile;
      setEditingId(uuid);
      setDraft({ label: p.label, description: p.description, profile: p.profile ?? '' });
      setModalOpen(true);
    } catch {
      setError('Failed to load profile.');
    }
  };

  const handleSave = async () => {
    if (!draft.label.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (editingId !== null) {
        await axios.put(`${GatewayIp}${endpoints.thirdparty.cambriaProfile(editingId)}`, draft, { headers: authHeaders });
      } else {
        await axios.post(`${GatewayIp}${endpoints.thirdparty.cambriaProfiles}`, draft, { headers: authHeaders });
      }
      setModalOpen(false);
      await loadProfiles();
    } catch {
      setError('Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    setError(null);
    try {
      await axios.delete(`${GatewayIp}${endpoints.thirdparty.cambriaProfile(deleteId)}`, { headers: authHeaders });
      setDeleteId(null);
      await loadProfiles();
    } catch {
      setError('Failed to delete profile.');
    }
  };

  const rows = profiles.map((profile) => ({
    id: profile.uuid,
    label: profile.label,
    description: profile.description || '—',
    actions: (
      <div className="settings-page__row-actions">
        <IconButton label="Edit profile" kind="ghost" size="sm" align="left" onClick={() => openEdit(profile.uuid)}>
          <Edit />
        </IconButton>
        <IconButton label="Delete profile" kind="ghost" size="sm" align="left" onClick={() => setDeleteId(profile.uuid)}>
          <TrashCan />
        </IconButton>
      </div>
    ),
  }));

  return (
    <>
      <div className="settings-page__list-panel" style={{ minWidth: 600, padding: 0, background: 'none' }}>
        <div className="settings-page__panel-header">
          <div />
          <Button size="sm" kind="primary" onClick={openCreate}>Add profile</Button>
        </div>

        {error && <p style={{ color: '#fa4d56', marginBottom: '0.5rem' }}>{error}</p>}
        <div className="settings-page__resources-table-wrap">
          {loading ? (
            <div className="settings-page__resources-loading">
              <InlineLoading description="Loading profiles..." />
            </div>
          ) : (
            <TableContainer title="">
              <DataTable
                rows={rows}
                headers={cambriaProfilesHeaders}
                render={({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => (
                  <Table {...getTableProps()} size="lg">
                    <TableHead>
                      <TableRow>
                        {headers.map((header) => (
                          <TableHeader key={header.key} {...getHeaderProps({ header })}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={headers.length}>No profiles found.</TableCell>
                        </TableRow>
                      ) : (
                        rows.map((row) => (
                          <TableRow key={row.id} {...getRowProps({ row })}>
                            {row.cells.map((cell) => (
                              <TableCell key={cell.id}>{cell.value}</TableCell>
                            ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              />
            </TableContainer>
          )}
        </div>
      </div>

      <Modal
        open={modalOpen}
        modalHeading={editingId !== null ? 'Edit profile' : 'Add profile'}
        primaryButtonText={saving ? 'Saving…' : 'Save'}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={saving || !draft.label.trim()}
        onRequestClose={() => setModalOpen(false)}
        onRequestSubmit={handleSave}
      >
        <div className="settings-page__form-grid">
          <TextInput
            id="cambria-profile-label"
            labelText="Label"
            value={draft.label}
            onChange={e => setDraft({ ...draft, label: e.target.value })}
          />
          <TextArea
            id="cambria-profile-description"
            labelText="Description"
            rows={3}
            value={draft.description}
            onChange={e => setDraft({ ...draft, description: e.target.value })}
          />
          <TextArea
            id="cambria-profile-xml"
            labelText="Profile XML"
            rows={12}
            value={draft.profile}
            onChange={e => setDraft({ ...draft, profile: e.target.value })}
            style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
          />
        </div>
      </Modal>

      <Modal
        open={deleteId !== null}
        danger
        modalHeading="Delete profile"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestClose={() => setDeleteId(null)}
        onRequestSubmit={handleDelete}
      >
        <p>Are you sure you want to delete this profile? This action cannot be undone.</p>
      </Modal>
    </>
  );
};

// ---- Endpoints Panel ----

const CambriaEndpointsPanel: React.FC<{ token: string | null }> = ({ token }) => {
  const [eps, setEps] = useState<CambriaEndpoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState({ label: '', baseUrl: '', apiKey: '', enabled: true });
  const [saving, setSaving] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadEndpoints = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${GatewayIp}${endpoints.thirdparty.cambriaEndpoints}`, { headers: authHeaders });
      const sorted = [...(res.data as CambriaEndpoint[])].sort((a, b) => b.priority - a.priority);
      setEps(sorted);
    } catch {
      setError('Failed to load endpoints.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadEndpoints(); }, [loadEndpoints]);

  const openCreate = () => {
    setEditingId(null);
    setDraft({ label: '', baseUrl: '', apiKey: '', enabled: true });
    setModalOpen(true);
  };

  const openEdit = (ep: CambriaEndpoint) => {
    setEditingId(ep.id);
    setDraft({ label: ep.label, baseUrl: ep.baseUrl, apiKey: ep.apiKey ?? '', enabled: ep.enabled });
    setModalOpen(true);
  };

  const rebalancePriorities = async () => {
    const res = await axios.get(`${GatewayIp}${endpoints.thirdparty.cambriaEndpoints}`, { headers: authHeaders });
    const sorted = [...(res.data as CambriaEndpoint[])].sort((a, b) => b.priority - a.priority);
    const updated = sorted.map((ep, i) => ({ ...ep, priority: sorted.length - 1 - i }));
    await Promise.all(
      updated.map(ep =>
        axios.put(`${GatewayIp}${endpoints.thirdparty.cambriaEndpoint(ep.id)}`, ep, { headers: authHeaders })
      )
    );
    setEps(updated);
  };

  const handleSave = async () => {
    if (!draft.label.trim()) return;
    setSaving(true);
    setError(null);
    const existingEp = editingId !== null ? eps.find(e => e.id === editingId) : null;
    const payload = {
      label: draft.label,
      baseUrl: draft.baseUrl,
      apiKey: draft.apiKey.trim() || null,
      enabled: draft.enabled,
      ...(existingEp ? { priority: existingEp.priority } : {}),
    };
    try {
      if (editingId !== null) {
        await axios.put(`${GatewayIp}${endpoints.thirdparty.cambriaEndpoint(editingId)}`, payload, { headers: authHeaders });
        await loadEndpoints();
      } else {
        await axios.post(`${GatewayIp}${endpoints.thirdparty.cambriaEndpoints}`, payload, { headers: authHeaders });
        await rebalancePriorities();
      }
      setModalOpen(false);
    } catch {
      setError('Failed to save endpoint.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    setError(null);
    try {
      await axios.delete(`${GatewayIp}${endpoints.thirdparty.cambriaEndpoint(deleteId)}`, { headers: authHeaders });
      setDeleteId(null);
      await rebalancePriorities();
    } catch {
      setError('Failed to delete endpoint.');
    }
  };

  const movePriority = async (index: number, direction: 'up' | 'down') => {
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= eps.length) return;

    const reordered = [...eps];
    [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];

    // Assign descending priorities: top item gets highest number so desc sort keeps order stable
    const updated = reordered.map((ep, i) => ({ ...ep, priority: reordered.length - 1 - i }));
    setEps(updated);

    setError(null);
    try {
      await Promise.all(
        updated.map(ep =>
          axios.put(`${GatewayIp}${endpoints.thirdparty.cambriaEndpoint(ep.id)}`, ep, { headers: authHeaders })
        )
      );
    } catch {
      setError('Failed to update priority.');
      loadEndpoints();
    }
  };

  const rows = eps.map((ep, i) => ({
    id: ep.id.toString(),
    priority: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        <span style={{ minWidth: 20, textAlign: 'right' }}>{ep.priority}</span>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <IconButton
            label="Move up"
            kind="ghost"
            size="sm"
            disabled={i === 0}
            onClick={() => movePriority(i, 'up')}
          >
            <ChevronUp />
          </IconButton>
          <IconButton
            label="Move down"
            kind="ghost"
            size="sm"
            disabled={i === eps.length - 1}
            onClick={() => movePriority(i, 'down')}
          >
            <ChevronDown />
          </IconButton>
        </div>
      </div>
    ),
    label: ep.label,
    baseUrl: <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{ep.baseUrl}</span>,
    apiKey: ep.apiKey ? '••••••••' : '—',
    status: (
      <span
        className="settings-page__chip"
        style={{
          backgroundColor: ep.enabled ? 'rgba(36,161,72,0.15)' : 'rgba(141,141,141,0.15)',
          color: ep.enabled ? '#42be65' : '#8d8d8d',
        }}
      >
        {ep.enabled ? 'Enabled' : 'Disabled'}
      </span>
    ),
    actions: (
      <div className="settings-page__row-actions">
        <IconButton label="Edit endpoint" kind="ghost" size="sm" align="left" onClick={() => openEdit(ep)}>
          <Edit />
        </IconButton>
        <IconButton label="Delete endpoint" kind="ghost" size="sm" align="left" onClick={() => setDeleteId(ep.id)}>
          <TrashCan />
        </IconButton>
      </div>
    ),
  }));

  return (
    <>
      <div className="settings-page__list-panel" style={{ minWidth: 600, padding: 0, background: 'none' }}>
        <div className="settings-page__panel-header">
          <div />
          <Button size="sm" kind="primary" onClick={openCreate}>Add endpoint</Button>
        </div>

        {error && <p style={{ color: '#fa4d56', marginBottom: '0.5rem' }}>{error}</p>}
        <div className="settings-page__resources-table-wrap">
          {loading ? (
            <div className="settings-page__resources-loading">
              <InlineLoading description="Loading endpoints..." />
            </div>
          ) : (
            <TableContainer title="">
              <DataTable
                rows={rows}
                headers={cambriaEndpointsHeaders}
                render={({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => (
                  <Table {...getTableProps()} size="lg">
                    <TableHead>
                      <TableRow>
                        {headers.map((header) => (
                          <TableHeader key={header.key} {...getHeaderProps({ header })}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={headers.length}>No endpoints found.</TableCell>
                        </TableRow>
                      ) : (
                        rows.map((row) => (
                          <TableRow key={row.id} {...getRowProps({ row })}>
                            {row.cells.map((cell) => (
                              <TableCell key={cell.id}>{cell.value}</TableCell>
                            ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              />
            </TableContainer>
          )}
        </div>
      </div>

      <Modal
        open={modalOpen}
        modalHeading={editingId !== null ? 'Edit endpoint' : 'Add endpoint'}
        primaryButtonText={saving ? 'Saving…' : 'Save'}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={saving || !draft.label.trim()}
        onRequestClose={() => setModalOpen(false)}
        onRequestSubmit={handleSave}
      >
        <div className="settings-page__form-grid">
          <TextInput
            id="cambria-ep-label"
            labelText="Label"
            value={draft.label}
            onChange={e => setDraft({ ...draft, label: e.target.value })}
          />
          <TextInput
            id="cambria-ep-baseurl"
            labelText="Base URL"
            placeholder="https://192.168.10.152:8648"
            value={draft.baseUrl}
            onChange={e => setDraft({ ...draft, baseUrl: e.target.value })}
          />
          <TextInput
            id="cambria-ep-apikey"
            labelText="API Key"
            placeholder="Leave empty for none"
            value={draft.apiKey}
            onChange={e => setDraft({ ...draft, apiKey: e.target.value })}
          />
          <Toggle
            id="cambria-ep-enabled"
            labelText="Enabled"
            labelA="Off"
            labelB="On"
            toggled={draft.enabled}
            onToggle={val => setDraft({ ...draft, enabled: val })}
          />
        </div>
      </Modal>

      <Modal
        open={deleteId !== null}
        danger
        modalHeading="Delete endpoint"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestClose={() => setDeleteId(null)}
        onRequestSubmit={handleDelete}
      >
        <p>Are you sure you want to delete this endpoint? This action cannot be undone.</p>
      </Modal>
    </>
  );
};

// ---- Main Thirdparty Tab ----

const ThirdpartyTab: React.FC = () => {
  const token = useAppSelector(selectToken);

  return (
    <div className="settings-page__panel">
      <div className="settings-page__panel-header">
        <div>
          <h3>Third-party Integrations</h3>
          <p>Manage external service integrations and their configuration.</p>
        </div>
      </div>

      <Tabs>
        <TabList aria-label="Third-party services" contained>
          <Tab>Cambria</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <Tabs>
              <TabList aria-label="Cambria sections" contained>
                <Tab>Profiles</Tab>
                <Tab>Endpoints</Tab>
              </TabList>
              <TabPanels>
                <TabPanel style={{ padding: 0 }}>
                  <CambriaProfilesPanel token={token} />
                </TabPanel>
                <TabPanel style={{ padding: 0 }}>
                  <CambriaEndpointsPanel token={token} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
};

export default ThirdpartyTab;
